import React from "react";
import { Modal, ModalFooter } from "reactstrap";
import { formatDateDDMMYY } from "../../utils";
import AparFullDetailContent, { APAR_DETAIL_SECTIONS } from "./AparFullDetailContent";
import "./AparFullDetailModal.css";

const ROLE_LABEL: any = {
  reporting: "Reporting Manager",
  reviewing: "Reviewing Manager",
  accepting: "Accepting Authority",
};

/**
 * Full read-only APAR details (same content as the "View Details" page) shown in a modal,
 * so managers can refer back to everything while on their final step.
 * Props: isOpen, toggle, viewerRole, employeeData, kpiReportId
 */
class AparFullDetailModal extends React.Component<any, any> {
  bodyRef: any = React.createRef();

  constructor(props: any) {
    super(props);
    this.state = { activeSection: APAR_DETAIL_SECTIONS[0].id };
  }

  scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    this.setState({ activeSection: id });
  };

  onBodyScroll = () => {
    const body = this.bodyRef.current;
    if (!body) return;
    const bodyTop = body.getBoundingClientRect().top;
    let current = this.state.activeSection;
    APAR_DETAIL_SECTIONS.forEach((s: any) => {
      const el = document.getElementById(s.id);
      if (el && el.getBoundingClientRect().top - bodyTop <= 80) current = s.id;
    });
    if (current !== this.state.activeSection) this.setState({ activeSection: current });
  };

  render() {
    const { isOpen, toggle, viewerRole, kpiReportId } = this.props;
    const employeeData = this.props.employeeData || {};
    const { activeSection } = this.state;
    const sections = APAR_DETAIL_SECTIONS.filter((s: any) => s.roles.includes(viewerRole));
    const period = employeeData.effectiveFrom
      ? `${formatDateDDMMYY(employeeData.effectiveFrom)} to ${employeeData.effectiveTo ? formatDateDDMMYY(employeeData.effectiveTo) : "Present"}`
      : "";

    return (
      <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable centered keyboard className="apar-detail-modal">
        {/* Header */}
        <div
          className="d-flex align-items-start justify-content-between px-4 py-3"
          style={{ background: "linear-gradient(90deg, #2f5fb3 0%, #3f7fd6 100%)", color: "#fff" }}
        >
          <div>
            <h5 className="mb-1 font-weight-bold" style={{ color: "#fff" }}>
              <i className="fa fa-file-text-o mr-2" />
              Complete Appraisal Details
            </h5>
            <div className="small" style={{ opacity: 0.9 }}>
              {employeeData.employeeName && <strong>{employeeData.employeeName}</strong>}
              {employeeData.employeeNumber && <span> ({employeeData.employeeNumber})</span>}
              {period && <span> &middot; APAR Period {period}</span>}
              {viewerRole in ROLE_LABEL && (
                <span className="badge badge-light ml-2" style={{ color: "#2f5fb3" }}>
                  Viewing as {ROLE_LABEL[viewerRole]}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="close"
            aria-label="Close"
            onClick={toggle}
            style={{ color: "#fff", opacity: 0.9, textShadow: "none", fontSize: 28, lineHeight: 1 }}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        {/* Section navigation */}
        {sections.length > 1 && (
          <div className="px-4 py-2 border-bottom bg-light d-flex flex-wrap" style={{ gap: 8 }}>
            {sections.map((s: any, i: number) => (
              <button
                key={s.id}
                type="button"
                className={`btn apar-detail-nav-btn ${activeSection === s.id ? "active" : ""}`}
                onClick={() => this.scrollToSection(s.id)}
              >
                <span className="apar-detail-nav-index">{i + 1}.</span>
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div className="modal-body px-4" ref={this.bodyRef} onScroll={this.onBodyScroll} style={{ overflowY: "auto" }}>
          {isOpen && (
            <AparFullDetailContent viewerRole={viewerRole} employeeData={employeeData} kpiReportId={kpiReportId} />
          )}
        </div>

        <ModalFooter className="py-2">
          <button type="button" className="btn btn-outline-secondary" onClick={toggle}>
            Close
          </button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default AparFullDetailModal;
